import React, { useContext } from 'react';
import { PriceDisplayTypeEnum, PriceDTO } from '@api/ringil-api.v2.tsx';
import { formatPrice } from '@utils/priceUtils.ts';
import COBadge, { BadgeType } from '@components/FigmaComponents/COBadge.tsx';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import SvgCoin from '@icons/coin-inverted.svg?react';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

type PriceTagProps = {
  priceDto: PriceDTO;
  priceInternalDto?: PriceDTO;
  badgeType?: BadgeType;
  badgeVariant?: 'filled' | 'outlined';
};

const ExtraCostsPriceTag: React.FC<PriceTagProps> = ({
                                             priceDto,
                                             priceInternalDto,
                                             badgeType = 'positive',
                                             badgeVariant = 'filled',
                                           }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const id = React.useMemo(() => {
    const uid =
      typeof crypto !== 'undefined' && (crypto as any).randomUUID
        ? (crypto as any).randomUUID()
        : Math.random().toString(36).slice(2, 9);
    return `id-${uid}`;
  }, []);

  const priceDisplayType = loggedUser?.priceDisplayType || PriceDisplayTypeEnum.NO_SHOW_ADDITIONAL_PRICE;

  const formatPriceDto = (priceDto: PriceDTO) => {
    if (!priceDto) return '';

    switch (priceDisplayType) {
      case PriceDisplayTypeEnum.SHOW_PREFERRED_PRICE:
        return formatPrice(priceDto.companyComputedPrice, priceDto.companyComputedCurrency);
      case PriceDisplayTypeEnum.SHOW_ORIGINAL_PRICE:
        return formatPrice(priceDto.price, priceDto.currency);
      case PriceDisplayTypeEnum.NO_SHOW_ADDITIONAL_PRICE:
      default:
        return formatPrice(priceDto.price, priceDto.currency);
    }
  };

  const formatTootlipPriceDto = (priceDto: PriceDTO) => {
    if (!priceDto) return '';

    switch (priceDisplayType) {
      case PriceDisplayTypeEnum.SHOW_PREFERRED_PRICE:
        return formatPrice(priceDto.price, priceDto.currency);
      case PriceDisplayTypeEnum.SHOW_ORIGINAL_PRICE:
        return formatPrice(priceDto.companyComputedPrice, priceDto.companyComputedCurrency);
      case PriceDisplayTypeEnum.NO_SHOW_ADDITIONAL_PRICE:
      default:
        return '';
    }
  };

  const tooltipEnabled = priceDisplayType !== PriceDisplayTypeEnum.NO_SHOW_ADDITIONAL_PRICE;

  const tooltipTitle = (() => {
    if (priceDisplayType === PriceDisplayTypeEnum.SHOW_PREFERRED_PRICE) return tr('PreferredPriceTag.originalPrice', 'Original Price');
    if (priceDisplayType === PriceDisplayTypeEnum.SHOW_ORIGINAL_PRICE) return tr('PreferredPriceTag.preferredPrice', 'Preferred Price');
    return '';
  })();

  const tooltipConversionRate = (() => {
    if (!priceDto) return '';

    const formattedRate = new Intl.NumberFormat(undefined, { maximumSignificantDigits: 3 }).format(priceDto.conversationRate);
    const label = tr('PreferredPriceTag.conversionRateLabel', 'Conversion rate');

    return `${label}: ${formattedRate} ${priceDto.companyComputedCurrency} / ${priceDto.currency}`;

  })();

  return (
    <div
      id={id}
    >
      {tooltipEnabled && (
        <CustomTooltip targetSelector={`#${id}`} content={
          <div
            className={`flex flex-col`}>
            <span className="pb-2">{tooltipTitle}</span>
            {
              priceInternalDto ? (
                <span className="inline-flex flex-wrap items-center gap-1">
                {tr('ExtraCostsPriceTag.label', '{{totalFormatedPrice}} (of which internal: {{totalInternalFormatedPrice}})', {
                  totalFormatedPrice: formatTootlipPriceDto(priceDto),
                  totalInternalFormatedPrice: formatTootlipPriceDto(priceInternalDto),
                })}
              </span>
              ) : (
                <span>{formatTootlipPriceDto(priceDto)}</span>
              )
            }
            {priceDto?.currency !== priceDto?.companyComputedCurrency && (
              <span>{tooltipConversionRate}</span>
            )}
          </div>
        } />
      )}
      <COBadge variant={badgeVariant} type={badgeType}>
        {
          priceInternalDto ? (
            <div className="flex flex-row items-center gap-2">
              <span className="inline-flex flex-wrap items-center gap-1">
                {tr('ExtraCostsPriceTag.label', '{{totalFormatedPrice}} (of which internal: {{totalInternalFormatedPrice}})', {
                  totalFormatedPrice: formatPriceDto(priceDto),
                  totalInternalFormatedPrice: formatPriceDto(priceInternalDto),
                })}
              </span>
              {tooltipEnabled && (
                <SvgCoin className="text-content-tertiary flex-shrink-0 self-center" />
              )}
            </div>
          ) : (
            <>
              <span>{formatPriceDto(priceDto)}</span>
              {tooltipEnabled && (
                <SvgCoin className="text-content-tertiary flex-shrink-0 self-center" />
              )}
            </>
          )
        }
      </COBadge>
    </div>
  );
};

export default ExtraCostsPriceTag;